import {
  TranslationModel,
  LanguagesModel,
  DomainModel,
} from '../models/transaltion.model';
import { ApiResponse } from '../models';
import http from '../utils/http';

interface BackendAuthToken {
  name: string;
  url: string;
  token: string;
}

const backendTokensStorageKey =
  'admin-backend-auth-tokens';

const primaryAuthBackendStorageKey =
  'primary-admin-auth-backend';

function getBackendAuthTokens(): BackendAuthToken[] {
  try {
    const stored = localStorage.getItem(
      backendTokensStorageKey,
    );

    if (!stored) {
      return [];
    }

    const parsed = JSON.parse(stored);

    return Array.isArray(parsed)
      ? parsed
      : [];
  } catch {
    return [];
  }
}

function getPrimaryBackend(): BackendAuthToken | null {
  try {
    const stored = localStorage.getItem(
      primaryAuthBackendStorageKey,
    );

    if (!stored) {
      return null;
    }

    const parsed = JSON.parse(stored);

    if (
      parsed &&
      parsed.name &&
      parsed.url &&
      parsed.token
    ) {
      return parsed;
    }

    return null;
  } catch {
    return null;
  }
}

function getTranslationBackend(): BackendAuthToken | null {
  const backends =
    getBackendAuthTokens();

  const primary =
    getPrimaryBackend();

  if (primary) {
    const backend =
      backends.find(
        (item) =>
          item.name === primary.name,
      );

    if (backend) {
      return backend;
    }
  }

  return backends[0] || null;
}

function getTranslationBackendConfig() {
  const backend =
    getTranslationBackend();

  if (!backend) {
    return null;
  }

  return {
    backend,
    config: {
      withCredentials: true,
      backend: backend.name,
      headers: {
        'Auth-Token': backend.token,
      },
    },
  };
}

// export async function serviceGetTranslations(): Promise<TranslationModel[]> {
//   const response: ApiResponse<TranslationModel[]> = await http.get(
//     `/api/static/i18n/all`,
//   );
//   return response.data;
// }

export async function serviceGetOneTranslation(
  groups: any[],
  locales: any,
) {
  const backendConfig =
    getTranslationBackendConfig();

  if (!backendConfig) {
    throw new Error(
      '[TRANSLATIONS] No authenticated backend available',
    );
  }

  const response: ApiResponse<any[]> =
    await http.get(
      `${backendConfig.backend.url.replace(
        /\/+$/,
        '',
      )}/api/static/i18n`,
      {
        ...backendConfig.config,
        params: {
          groups,
          locales,
        },
      },
    );

  return response.data;
}

export async function serviceGetTranslations(
  groups: any[] = [],
  locales: any[] = [],
) {
  const backendConfig =
    getTranslationBackendConfig();

  if (!backendConfig) {
    throw new Error(
      '[TRANSLATIONS] No authenticated backend available',
    );
  }

  const response: ApiResponse<any[]> =
    await http.get(
      `${backendConfig.backend.url.replace(
        /\/+$/,
        '',
      )}/api/static/i18n/admin`,
      {
        ...backendConfig.config,
        params: {
          groups,
          locales,
        },
      },
    );

  return response.data;
}

export async function serviceGetAllLanguages(): Promise<
  LanguagesModel[]
> {
  const backendConfig =
    getTranslationBackendConfig();

  if (!backendConfig) {
    throw new Error(
      '[TRANSLATIONS] No authenticated backend available',
    );
  }

  const response: ApiResponse<
    LanguagesModel[]
  > = await http.get(
    `${backendConfig.backend.url.replace(
      /\/+$/,
      '',
    )}/api/static/i18n/locales`,
    backendConfig.config,
  );

  return response.data;
}

export async function serviceDeleteTranslation(
  id_translation: number,
): Promise<ApiResponse<any>> {
  const backendConfig =
    getTranslationBackendConfig();

  if (!backendConfig) {
    throw new Error(
      '[TRANSLATIONS] No authenticated backend available',
    );
  }

  return await http.delete(
    `${backendConfig.backend.url.replace(
      /\/+$/,
      '',
    )}/api/static/i18n/id/${id_translation}`,
    {
      ...backendConfig.config,
      headers: {
        ...backendConfig.config.headers,
        successAlert: true,
      },
    },
  );
}

export async function serviceCreateNewTranslation(
  translation: Partial<TranslationModel>,
): Promise<TranslationModel> {
  const backendConfig =
    getTranslationBackendConfig();

  if (!backendConfig) {
    throw new Error(
      '[TRANSLATIONS] No authenticated backend available',
    );
  }

  const response: ApiResponse<TranslationModel> =
    await http.post(
      `${backendConfig.backend.url.replace(
        /\/+$/,
        '',
      )}/api/static/i18n/translation`,
      translation,
      {
        ...backendConfig.config,
        headers: {
          ...backendConfig.config.headers,
          successAlert: true,
        },
      },
    );

  return response.data;
}

export async function serviceGetAllGroups(): Promise<
  DomainModel[]
> {
  const backendConfig =
    getTranslationBackendConfig();

  if (!backendConfig) {
    throw new Error(
      '[TRANSLATIONS] No authenticated backend available',
    );
  }

  const response: ApiResponse<
    DomainModel[]
  > = await http.get(
    `${backendConfig.backend.url.replace(
      /\/+$/,
      '',
    )}/api/static/i18n/group/all`,
    backendConfig.config,
  );

  return response.data;
}

export async function serviceEditTranslation(
  translation: Partial<TranslationModel>,
): Promise<ApiResponse<any>> {
  const backendConfig =
    getTranslationBackendConfig();

  if (!backendConfig) {
    throw new Error(
      '[TRANSLATIONS] No authenticated backend available',
    );
  }

  return await http.put(
    `${backendConfig.backend.url.replace(
      /\/+$/,
      '',
    )}/api/static/i18n/id/${translation.id}`,
    translation,
    {
      ...backendConfig.config,
      headers: {
        ...backendConfig.config.headers,
        successAlert: true,
      },
    },
  );
}

export async function serviceInvalidateTranslationCache() {
  const backendConfig =
    getTranslationBackendConfig();

  if (!backendConfig) {
    throw new Error(
      '[TRANSLATIONS] No authenticated backend available',
    );
  }

  const response: ApiResponse<any> =
    await http.get(
      `${backendConfig.backend.url.replace(
        /\/+$/,
        '',
      )}/api/static/i18n/save`,
      {
        ...backendConfig.config,
        headers: {
          ...backendConfig.config.headers,
          successAlert: true,
        },
      },
    );

  return response.data;
}